import React, { useCallback, useState } from 'react';
import { Button } from 'is24-corecss';
import { Modal } from '../../../../common/display/modal/Modal';
import { Benefit } from '../../../../../@types/benefits';
import type { BenefitModalProps } from '../../../../../@types/benefitModalProps';
import MietrechtsberatungJpg from './images/rent_legal.jpg';
import MietrechtsberatungWebp from './images/rent_legal.webp';
import MietrechtsberatungMobileJpg from './images/rent_legal_mobile.jpg';
import MietrechtsberatungMobileWebp from './images/rent_legal_mobile.jpg';
import { EntitlementType } from '../../../../../@types/entitlement';
import { useGetHasEntitlement } from '../../../../../customHooks/useGetHasEntitlement.hook';
import { ProductsStatusKey } from '../../../../../@types/productsStatus';
import MietrechtsberatungSubmitForm from './components/MietrechtsberatungSubmitForm';
import HowItWorksTab from './components/HowItWorksTab';
import OverviewTab from './components/OverviewTab';
import { OverviewTrackingEvents } from '../../../../../tracking/trackingEvents';
import { useHasMeinPlus } from '../../../../../customHooks/useHasMeinPlus.hook';
import { report } from '../../../../../tracking/tracking';
import type { ProfileBenefitsStatusResponse } from '../../../../../@types/productBenefits';
import { ProfileBenefitType } from '../../../../../@types/productBenefits';
import {
  useGetProductBenefitsPersonalData,
  useProductBenefitsGrantBenefit,
  useProductBenefitsStartContract,
} from '../../../../../apiHooks/productBenefits.hook';
import { useProductStatus } from '../../../../../customHooks/useProductStatus.hook';
import { useIntl } from '@/i18n';

const BASIC_URL =
  '/warenkorb/wohnenplus?cmp_id=10-052071&cmp_name=premium_profile_living&cmp_position=residential_benefits&cmp_creative=legaladvice_cta';

export const Mietrechtsberatung = ({ isVisible, onClose }: BenefitModalProps) => {
  const { t } = useIntl();
  const [showForm, setShowForm] = useState(false);
  const handleCloseForm = () => setShowForm(false);
  const isEntitled = useGetHasEntitlement(EntitlementType.KEY_AND_LEGAL_SERVICE);
  const { initializing } = useGetProductBenefitsPersonalData();
  const product = useProductStatus(ProductsStatusKey.ProfileBenefits);
  const rentLegalAdviceService = (product as ProfileBenefitsStatusResponse)?.rentLegalAdvice;
  const isMeinPlus = useHasMeinPlus();
  const { mutate, isLoading } = useProductBenefitsGrantBenefit();
  const { mutate: mutateProductBenefitsStart, isLoading: isLoadingProductBenefitsStart } =
    useProductBenefitsStartContract();

  const showLoading = isLoading || isLoadingProductBenefitsStart || initializing;

  const handleOpenForm = useCallback(async () => {
    await report(OverviewTrackingEvents.RENT_LEGAL.FORM_VISITED, isMeinPlus);
    setShowForm(true);
  }, [setShowForm, isMeinPlus]);

  const handleSubmitCallback = useCallback(
    async (values: any) => {
      try {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { policyNumber, ...valuesToSubmit } = values;
        await mutateProductBenefitsStart({
          benefitType: ProfileBenefitType.RENT_LEGAL_ADVICE,
          formData: valuesToSubmit,
        });
      } catch {
        await mutate(ProfileBenefitType.RENT_LEGAL_ADVICE);
      } finally {
        await report(OverviewTrackingEvents.RENT_LEGAL.FUNNEL_SUBMIT, isMeinPlus);
        setShowForm(false);
      }
    },
    [isMeinPlus, mutate, mutateProductBenefitsStart],
  );

  const renderButtonArea = useCallback(() => {
    if (rentLegalAdviceService || showForm) {
      return null;
    }

    return (
      <div className='align-center'>
        {isEntitled ? (
          <Button onClick={handleOpenForm} appearance='secondary'>
            {t({
              id: 'benefits.rentLegal.modal.activeButton',
              defaultMessage: 'Mietrechtsberatung aktivieren',
            })}
          </Button>
        ) : (
          <a className={'button-secondary'} href={BASIC_URL}>
            {t({ id: 'benefits.rentLegal.modal.linkButton', defaultMessage: 'WohnenPlus entdecken' })}
          </a>
        )}
      </div>
    );
  }, [showForm, isEntitled, rentLegalAdviceService]);

  return (
    <Modal
      title={t({ id: 'benefits.rentLegal.modal.headline', defaultMessage: 'Mietrechtsberatung' })}
      isVisible={isVisible}
      image={MietrechtsberatungJpg}
      imageWebp={MietrechtsberatungWebp}
      imageMobile={MietrechtsberatungMobileJpg}
      imageMobileWebp={MietrechtsberatungMobileWebp}
      imageUseFullWidth={true}
      imageAlt={t({
        id: 'benefits.rentLegal.modal.image',
        defaultMessage: 'Woman using mortgage manager',
      })}
      tabsContent={[
        <OverviewTab
          key='benefits.rentLegal.modal.overview'
          isEntitled={isEntitled}
          isRentLegalAdviceService={rentLegalAdviceService}
        />,
        <HowItWorksTab key='benefits.rentLegal.modal.howItWorks' />,
      ]}
      formData={
        showForm ? (
          <MietrechtsberatungSubmitForm
            close={handleCloseForm}
            onSubmit={handleSubmitCallback}
            isLoading={showLoading}
          />
        ) : null
      }
      buttonArea={renderButtonArea()}
      benefit={Benefit.RENT_LEGAL}
      onClose={onClose}
    />
  );
};
